import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { joinWaitlist } from '../../util/api';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, ResponsiveImage } from '../../components';

import WaitlistForm from './WaitlistForm/WaitlistForm';
import { IconCalendar, IconGuests, IconHome, IconMapPin, IconShieldCheck } from './WaitlistIcons';
import heroImage from './images/waitlist.jpeg';

import css from './WaitlistPage.module.css';

// Roles map to Brevo contact lists in server/api/waitlist.js
const ROLE_HOST = 'host';
const ROLE_GUEST = 'guest';

const FEATURES = [
  { id: 'byTheHour', Icon: IconCalendar },
  { id: 'uniqueSpaces', Icon: IconMapPin },
  { id: 'protected', Icon: IconShieldCheck },
  { id: 'simpleAndSecure', Icon: IconGuests },
];

// Line breaks follow the design on desktop. On smaller screens text wraps naturally.
const lineBreak = <br className={css.desktopLineBreak} />;

/**
 * Marketplace logo from the branding configuration (hosted branding asset or local fallback).
 *
 * @param {Object} props
 * @param {string?} props.className image class
 * @param {Object} props.config marketplace configuration
 * @returns {JSX.Element} logo image
 */
const WaitlistLogo = props => {
  const { className, config } = props;
  const { logoImageDesktop } = config.branding;
  const alt = config.marketplaceName;

  if (logoImageDesktop?.type === 'imageAsset') {
    const variants = logoImageDesktop.attributes.variants;
    return (
      <ResponsiveImage
        rootClassName={className}
        alt={alt}
        image={logoImageDesktop}
        variants={Object.keys(variants)}
        sizes="245px"
      />
    );
  }

  return <img className={className} src={logoImageDesktop} alt={alt} />;
};

/**
 * Signup card for one waitlist role (host or guest).
 *
 * @param {Object} props
 * @param {string} props.role waitlist role: 'host' or 'guest'
 * @param {string} props.messagePrefix translation key prefix for the card texts
 * @param {Function} props.Icon card icon component
 * @param {string} props.marketplaceName marketplace name used in the texts
 * @returns {JSX.Element} signup card
 */
const WaitlistCard = props => {
  const { role, messagePrefix, Icon, marketplaceName } = props;
  const titleId = `waitlist-${role}-title`;

  return (
    <section className={css.card} aria-labelledby={titleId}>
      <div className={css.cardHeader}>
        <Icon className={css.cardIcon} strokeWidth={1.6} />
        <div>
          <h2 id={titleId} className={css.cardTitle}>
            <FormattedMessage id={`${messagePrefix}.title`} />
          </h2>
          <p className={css.cardDescription}>
            <FormattedMessage
              id={`${messagePrefix}.description`}
              values={{ lineBreak, marketplaceName }}
            />
          </p>
        </div>
      </div>
      <WaitlistForm
        formId={`WaitlistForm.${role}`}
        submitButtonText={<FormattedMessage id={`${messagePrefix}.submit`} />}
        onSubmit={values => joinWaitlist({ email: values.email, company: values.company, role })}
      />
    </section>
  );
};

/**
 * Temporary "coming soon" page that collects host and guest emails to Brevo
 * through the server endpoint /api/waitlist. Content is static (translations),
 * so the page doesn't need a loadData function.
 *
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @returns {JSX.Element} waitlist page
 */
export const WaitlistPageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const { scrollingDisabled } = props;
  const { marketplaceName } = config;

  const title = intl.formatMessage({ id: 'WaitlistPage.schemaTitle' }, { marketplaceName });
  const description = intl.formatMessage(
    { id: 'WaitlistPage.schemaDescription' },
    { marketplaceName }
  );

  return (
    <Page
      className={css.page}
      title={title}
      description={description}
      scrollingDisabled={scrollingDisabled}
    >
      <main className={css.root}>
        <div className={css.hero}>
          <div className={css.heroContent}>
            <WaitlistLogo className={css.logo} config={config} />

            <p className={css.eyebrow}>
              <FormattedMessage id="WaitlistPage.eyebrow" />
            </p>
            <h1 className={css.heading}>
              <FormattedMessage
                id="WaitlistPage.heading"
                values={{
                  highlight: (
                    <span className={css.highlight}>
                      <FormattedMessage id="WaitlistPage.headingHighlight" />
                    </span>
                  ),
                }}
              />
            </h1>
            <p className={css.lead}>
              <FormattedMessage id="WaitlistPage.lead" values={{ marketplaceName, lineBreak }} />
            </p>

            {/* Hero photo: background of the whole hero on desktop, a banner on smaller screens */}
            <div className={css.heroMedia}>
              <img
                className={css.heroImage}
                src={heroImage}
                alt=""
                width={1672}
                height={941}
                loading="eager"
                fetchpriority="high"
              />
            </div>

            <div className={css.cards}>
              <WaitlistCard
                role={ROLE_HOST}
                messagePrefix="WaitlistPage.host"
                Icon={IconHome}
                marketplaceName={marketplaceName}
              />
              <WaitlistCard
                role={ROLE_GUEST}
                messagePrefix="WaitlistPage.guest"
                Icon={IconGuests}
                marketplaceName={marketplaceName}
              />
            </div>
          </div>
        </div>

        <ul className={css.features}>
          {FEATURES.map(({ id, Icon }) => (
            <li key={id} className={css.feature}>
              <Icon className={css.featureIcon} strokeWidth={1.5} />
              <h2 className={css.featureTitle}>
                <FormattedMessage id={`WaitlistPage.feature.${id}.title`} />
              </h2>
              <p className={css.featureDescription}>
                <FormattedMessage
                  id={`WaitlistPage.feature.${id}.description`}
                  values={{ lineBreak }}
                />
              </p>
            </li>
          ))}
        </ul>
      </main>
    </Page>
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const WaitlistPage = compose(connect(mapStateToProps))(WaitlistPageComponent);

export default WaitlistPage;
